import { useEffect, useRef, useState } from 'react';

/** `off`: not drawn. `on`: drawn. `out`: still drawn and fading, about to be `off`. */
export type LoaderPhase = 'off' | 'on' | 'out';

/**
 * When a page's loader is shown, for how long, and how it leaves.
 *
 * It was shown after 300 ms and hidden the moment the data arrived, which on a fast page is never
 * and on a slow one is a glimpse: the loader was on screen for less than one stroke of its own
 * motion. So:
 *
 *   `delay`       wait this long before showing it. Under ~150 ms nobody sees a page as loading,
 *                 and a loader that blinks for that long is noise.
 *   `minVisible`  once shown, keep it at least this long, so it finishes drawing and fills. It sits
 *                 beside the page's title and does not hold any content back.
 *   `fade`        leave by fading, not by vanishing.
 *
 * Asking again while it is fading brings it back at once.
 */
export function useLoaderPhase(
  active: boolean,
  { delay = 150, minVisible = 1100, fade = 220 }: { delay?: number; minVisible?: number; fade?: number } = {},
): LoaderPhase {
  const [phase, setPhase] = useState<LoaderPhase>('off');
  const shownAt = useRef(0);

  useEffect(() => {
    if (active) {
      if (phase === 'out') {
        setPhase('on');
        return;
      }
      if (phase === 'on') return;
      const t = window.setTimeout(() => {
        shownAt.current = performance.now();
        setPhase('on');
      }, delay);
      return () => window.clearTimeout(t);
    }
    if (phase === 'on') {
      const left = Math.max(0, minVisible - (performance.now() - shownAt.current));
      const t = window.setTimeout(() => setPhase('out'), left);
      return () => window.clearTimeout(t);
    }
    if (phase === 'out') {
      const t = window.setTimeout(() => setPhase('off'), fade);
      return () => window.clearTimeout(t);
    }
  }, [active, phase, delay, minVisible, fade]);

  return phase;
}
